"use client";

import { Check, ShieldCheck, Zap, Calendar, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhyChooseOurService() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const benefits = [{
    title: "All Major Brands",
    description: "Expert servicing and repairs for every leading manufacturer in the industry, from Honda to Stihl.",
    icon: ShieldCheck
  }, {
    title: "Fast Turnaround",
    description: "Minimising your downtime with efficient diagnostics and rapid repair services when you need them most.",
    icon: Zap
  }, {
    title: "Seasonal Packages",
    description: "Tailored maintenance plans to keep your machinery ready for every season, ensuring peak performance.",
    icon: Calendar
  }, {
    title: "Local to Carlisle",
    description: "Proudly serving the local community with reliable, on-site support and expert technical advice.",
    icon: MapPin
  }];
  return <section data-section-id="3069" id="why-choose-our-service" className="py-24 lg:py-32 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full opacity-[0.03] pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-primary blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-primary blur-[120px]" />
      </div>

      <div className="page-container relative z-10">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="max-w-3xl mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary font-sans text-xs font-bold tracking-wider uppercase mb-6">
              <Check className="w-3.5 h-3.5" />
              <span>The Carlisle Standard</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-foreground mb-8">
              Reliable Repairs from Engineers Who <span className="text-primary italic">Know</span> Their Machinery
            </h2>
            <p className="font-sans text-lg md:text-xl leading-relaxed text-muted-foreground max-w-2xl">
              We provide expert servicing and repairs for all major brands of lawn mowers and garden machinery. From routine maintenance to complex engine repairs, our Carlisle-based engineers ensure your equipment is reliable and ready for work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-16">
            {benefits.map((benefit, i) => <div key={benefit.title} data-index={i} className="group bg-card p-8 rounded-lg shadow-md border border-border hover:border-primary/30 transition-all duration-500 hover:shadow-lg flex flex-col">
                <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-8 bg-muted group-hover:bg-primary/10 transition-colors duration-300">
                  <benefit.icon className="w-7 h-7 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
                </div>
                <h3 className="font-serif text-2xl mb-4 text-foreground group-hover:text-primary transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="font-sans text-muted-foreground leading-relaxed mb-6 flex-grow">
                  {benefit.description}
                </p>
                <div className="h-1 w-12 bg-primary/20 group-hover:w-full transition-all duration-500 rounded-lg" />
              </div>)}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-6 pt-8 border-t border-border">
            <Button asChild size="lg" className="rounded-lg px-8 font-sans font-semibold tracking-wide">
              <Link to="/AboutPage">Learn About Our Expertise</Link>
            </Button>
            <p className="font-sans text-sm text-muted-foreground">
              Trusted by homeowners and professionals across Cumbria.
            </p>
          </div>
        </div>
      </div>
    </section>;
}